import React, { useEffect, useState } from 'react';

import * as api from './AdminApi';
import * as notifications from 'lib/notifications';
import { BefattningDto, PermissionType, UserDto } from 'models/index';

import './AdminContainer.css';

import { Alert, Button, ButtonGroup, Card, Container, Dropdown, DropdownButton, Fade, Stack } from 'react-bootstrap';
import UserDtoView from 'components/Common/UserDtoView';
import AccessControlForm from './AccessControlForm';
import { getStandardRights } from './getStandardRights';
import { RoleType } from './RoleType';
import FmtDate from 'components/Common/FmtDate';
import { AdminUserForm, IAdminUserForm } from './AdminUserForm';
import { t } from 'components/Common/Translations';
import { useConfirm } from 'components/Common/Confirm/useConfirm';
import { instanceAdminUserListRequest } from 'lib/api';

export function BefattningList(props: {}) {

    // Common stuff from context
    // const { selectedFolder, parentFiles, selectedFolderIsWriteable, renameItem, refreshFileList, files } = useFiles();

    const [items, setItems] = useState<BefattningDto[]>(null);
    const [itemsChanged, setItemsChanged] = useState<BefattningDto[]>([]);
    const [editedItem, setEditedItem] = useState<BefattningDto>(null);

    const [users, setUsers] = useState<UserDto[] | null>(null);
    const [availableUsers, setAvailableUsers] = useState<UserDto[]>([]);

    // Om vi har en befattning som vi editerar, så hamnar den högst upp
    useEffect(() => {
        if (editedItem && items?.length > 0) {
            // Om editedItem inte längre finns, ta bort den ur listan.
            if (!items.find(b => b.user.id == editedItem.user.id)) {
                setEditedItem(null);
            }
        }
    }, [editedItem, items]);

    let sortedItems = items;
    if (editedItem != null) {
        sortedItems = [editedItem, ...items.filter(x => x.id != editedItem.id)];
    }

    // Calculate available users
    useEffect(() => {
        setAvailableUsers([]);
        let tmp: UserDto[] = [];
        if (users && items) {
            tmp = users.filter(x => !items.find(befattning => befattning.user.id == x.id));
        }
        setAvailableUsers(tmp);
    }, [users, items]);

    // Load data initially
    useEffect(() => {
        refreshBefattningList();
        loadUsers();
    }, []);

    function refreshBefattningList() {
        api.befattningList()
            .then(data => {
                //console.log("got data", data);
                data.sort((a, b) => a.id - b.id);
                setItems(data);
            }).catch((reason) => {
                //console.log("catch", reason);
                notifications.dispatchFromError("Fel vid listning av befattningar", reason, 15);
            });
    }

    function loadUsers() {
        instanceAdminUserListRequest()
            .then(data => {
                //console.log("got data", data);
                setUsers(data);
            }).catch((reason) => {
                //console.log("catch", reason);
                notifications.dispatchFromError("Fel vid listning av användare", reason, 15);
            });
    }

    const { confirm } = useConfirm();

    async function deleteItem(value: BefattningDto) {
        if (!await confirm(t("Är du säker på att du vill ta bort användarens rättigheter?"))) {
            return;
        }

        api.befattningDelete(value).then(data => {
            //console.log("got data", data);
            notifications.dispatch("Användarens rättigheter togs bort", 5);
            refreshBefattningList();
        }).catch((reason) => {
            //console.log("catch", reason);
            notifications.dispatchFromError("Fel vid borttagning", reason, 30);
        });
    }

    function updateItems(value: BefattningDto[]) {
        api.befattningPostBatch({ items: value }).then(data => {
            notifications.dispatch("Posten uppdaterades", 5);

            setItemsChanged([]);

            let newItems = [...items];
            data.items.forEach(element => {
                newItems = newItems.filter(x => x.id !== element.id);
                newItems.push(element);
            });
            newItems.sort((a, b) => a.id - b.id);

            setItems(newItems);
        }).catch((reason) => {
            //console.log("catch", reason);
            notifications.dispatchFromError("Fel vid uppdatering", reason, 30);
        });
    }

    function onFormIsChanged(isChanged: boolean, model: BefattningDto) {
        if (isChanged) {
            let newItems = [...itemsChanged, model];
            setItemsChanged(newItems);
        } else {
            let newItems = [...itemsChanged];
            newItems = newItems.filter(x => x.id != model.id);
            setItemsChanged(newItems);
        }
    }

    const [newUser, setNewUser] = useState(false);
    const [roleType, setRoleType] = useState<RoleType>(RoleType.Undefined);
    function newUserClicked(roleType?: RoleType) {
        setNewUser(true);
        setRoleType(roleType);
    }

    async function newUserSave(value: IAdminUserForm) {
        console.log("newUserSave", value);
        try {
            let user: UserDto = null;
            if (value.userId) {
                user = users.find(x => x.id == value.userId);
            }
            if (!user) {
                const request = {
                    email: value.email,
                    firstName: value.firstName,
                    lastName: value.lastName
                }
                const userResponse = await api.userPost(request);
                user = userResponse?.user;
            }

            let befattning: BefattningDto = null;
            let existingBefattningList = items.filter(x => x.user.id == user.id);
            if (existingBefattningList.length == 0) {
                befattning = { id: 0, permissions: [] as PermissionType[], user: user }
                if (roleType) {
                    let permissions = await getStandardRights(roleType);
                    befattning.permissions = permissions;
                }
                befattning = await api.befattningPost(befattning);
                notifications.dispatch("Data sparades", 5);

                setItems([befattning, ...items]);
            } else {
                befattning = existingBefattningList[0];
                notifications.dispatch("Användaren finns redan hos företag", 5);
            }
            setNewUser(false);
            setRoleType(RoleType.Undefined);

            setEditedItem(befattning);
        } catch (reason) {
            notifications.dispatchFromError("Data sparandes ej", reason, 30);
        }
    }

    return <>
        <h3>Användare & rättigheter</h3>

        {/* <div className="mx-auto">
            <Alert variant="info">
                <Alert.Heading>Antal ändrade poster: {itemsChanged.length} / {items?.length}</Alert.Heading>
                <p>
                    Du kan spara varje post individuellt eller spara alla ändringar på samma gång.
                </p>
                <hr />
                <p className="mb-0">
                </p>


            </Alert>
        </div> */}

        <Stack direction="horizontal" gap={2} className='mb-3'>
            <DropdownButton
                as={ButtonGroup}
                key="up"
                drop="down"
                variant="secondary"
                title={`Ny användare`}
            >
                <Dropdown.Item eventKey="1" onClick={() => newUserClicked(RoleType.AnkomstRegistrerare)}>Ankomstregistrerare</Dropdown.Item>
                <Dropdown.Item eventKey="2" onClick={() => newUserClicked(RoleType.Attestant)}>Attestant</Dropdown.Item>
                <Dropdown.Item eventKey="3" onClick={() => newUserClicked(RoleType.Revisor)}>Revisor</Dropdown.Item>
                <Dropdown.Item eventKey="3" onClick={() => newUserClicked(RoleType.Undefined)}>Utan rättigheter</Dropdown.Item>
            </DropdownButton>
        </Stack>

        <fieldset>
            <div className="d-block d-sm-block">
                {sortedItems && sortedItems.map((value, index) => (
                    <Card key={value.id} className='mb-3'>
                        <Card.Body>
                            <Card.Title><UserDtoView value={value.user} /></Card.Title>
                            <Card.Subtitle className="mb-2 text-muted">{value.user.email}</Card.Subtitle>

                            {(value.fromDate || value.toDate) && (
                                <p>
                                    Tidsbegränsad&nbsp;
                                    {value.fromDate && (<><span>från</span> <FmtDate value={value.fromDate} /></>)}
                                    {value.toDate && (<><span>till</span> <FmtDate value={value.toDate} /></>)}
                                    <br />
                                </p>
                            )}
                            <AccessControlForm onSubmitBefattning={x => { updateItems([x]); }} initialBefattning={value} id={value.id.toString()}
                                onFormIsChanged={onFormIsChanged} onDeleteBefattning={x => { deleteItem(x); }} />
                        </Card.Body>
                    </Card>
                ))}
            </div>
            {items && items.length < 1 && ("Inga befattningar hittades.")}

        </fieldset>


        <Fade in={itemsChanged.length > 0}>
            <div className="position-fixed bottom-0 start-0 w-100 text-dark bg-white py-3 shadow">
                <Container>
                    <Stack direction="horizontal" gap={2} className='mb-1'>
                        <Button variant="primary" disabled={itemsChanged.length == 0} onClick={x => updateItems(itemsChanged)}
                            title='Du kan spara varje post individuellt eller spara alla ändringar på samma gång.'>
                            Spara alla
                        </Button>
                        Antal ändrade poster: {itemsChanged.length > 0 ? `${itemsChanged.length}/${items?.length}` : ""}
                    </Stack>
                </Container>
            </div>
        </Fade>


        {newUser && (<AdminUserForm cancel={() => { setNewUser(false); }} submit={newUserSave} users={availableUsers} />)}
    </>
}
